import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpenCheck } from "lucide-react";
import { toast } from "sonner";
import { SkeletonRows } from "@/components/app/Common";
import { confirmAction } from "@/components/app/Confirm";
import { api, get, errMsg } from "@/lib/api";
import { BankHeader, BankNotices, DrawSettings, QuestionList } from "@/pages/job/bank/BankParts";
import { useMiniTour } from "@/components/app/miniTours";

const useBank = (jobId) => useQuery({ queryKey: ["qbank", jobId], queryFn: () => get(`/jobs/${jobId}/question-bank`),
  refetchInterval: (q) => (q.state.data?.status === "generating" ? 2000 : false) });

export function QuestionBankTab({ job, staff }) {
  const qc = useQueryClient();
  const { data, isLoading } = useBank(job.id);
  const [busy, setBusy] = useState(false);
  useMiniTour("bank", !!data);
  if (isLoading || !data) return <SkeletonRows rows={6} />;
  const put = (d) => qc.setQueryData(["qbank", job.id], d);
  const run = async (fn, msg) => { setBusy(true); try { put((await fn()).data); if (msg) toast.success(msg); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  const generate = async (again) => {
    if (again && !(await confirmAction({ title: "Regenerate the question bank?", body: "All questions are rewritten from the job description. Interviews already created keep their questions.", confirmLabel: "Regenerate" }))) return;
    run(() => api.post(`/jobs/${job.id}/question-bank/generate`), "Writing questions in the background");
  };
  const remove = (q) => run(() => api.delete(`/jobs/${job.id}/question-bank/questions/${q.id}`), "Question removed");
  return (
    <div className="space-y-5" data-testid="question-bank-tab">
      <BankHeader data={data} staff={staff} onGenerate={generate} busy={busy} />
      <BankNotices data={data} job={job} staff={staff} onRetry={() => generate(false)} />
      {data.questions.length > 0 && <DrawSettings key={`${data.draw.hr}-${data.draw.legal}`} data={data} staff={staff} onSave={(d) => run(() => api.put(`/jobs/${job.id}/question-bank/draw`, d), "Saved")} />}
      {data.questions.length > 0 && <QuestionList data={data} staff={staff} onRemove={remove} />}
    </div>
  );
}

export function QuestionBankCard({ job }) {
  const { data } = useBank(job.id);
  if (!data) return null;
  const text = data.status === "ready" ? `${data.questions.length} questions ready · each candidate gets ${data.draw.hr} HR + ${data.draw.legal} Legal`
    : data.status === "generating" ? "Writing questions from the job description…" : data.status === "failed" ? data.error_message : "Not generated yet — it's created automatically before the first interview.";
  return (
    <div className="panel flex flex-wrap items-center gap-3 p-4 text-sm" data-testid="workspace-bank-card">
      <BookOpenCheck size={16} className="text-brand" aria-hidden="true" />
      <span className="font-medium">Question bank</span><span className="text-ink-secondary">{text}</span>
      <Link to={`/app/jobs/${job.id}/question-bank`} className="ml-auto font-medium text-brand hover:underline" data-testid="workspace-bank-link">Open question bank</Link>
    </div>
  );
}
